import { useState } from 'react';
import { getFreshAccessToken } from '../../utils/authUtils';
import { formatCurrencyInput, extractNumericInput } from '../../utils/numberFormat';
import { api } from '@expat-ui/shared';
import {
    Country,
    Currency,
    AccountType,
    ACCOUNT_TYPE_LABELS,
    COUNTRY_ACCOUNT_TYPES,
    COUNTRY_CURRENCIES,
} from '../../types/api.types';
import styles from './AddAssetModal.module.scss';

interface AddAssetModalProps {
    defaultCountry: Country;
    onClose: () => void;
    onAdded: () => void;
}

const COUNTRIES: { value: Country; label: string }[] = [
    { value: 'INDIA', label: 'India' },
    { value: 'US', label: 'United States' },
    { value: 'CAN', label: 'Canada' },
];

export function AddAssetModal({ defaultCountry, onClose, onAdded }: AddAssetModalProps) {
    const [country, setCountry] = useState<Country>(defaultCountry);
    const [accountType, setAccountType] = useState<AccountType>(COUNTRY_ACCOUNT_TYPES[defaultCountry][0]);
    const [label, setLabel] = useState('');
    const [balance, setBalance] = useState('');
    const [expectedReturn, setExpectedReturn] = useState('7');
    const [ownership, setOwnership] = useState<'SELF' | 'SPOUSE' | 'JOINT'>('SELF');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const currency: Currency = COUNTRY_CURRENCIES[country];
    const accountTypes = COUNTRY_ACCOUNT_TYPES[country];

    const handleCountryChange = (newCountry: Country) => {
        setCountry(newCountry);
        setAccountType(COUNTRY_ACCOUNT_TYPES[newCountry][0]);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const token = await getFreshAccessToken();
            if (!token) throw new Error('Not authenticated');
            await api.post(
                '/assets',
                {
                    country,
                    currency,
                    accountType,
                    label,
                    balance: Number(balance),
                    expectedReturn: Number(expectedReturn),
                    ownership,
                },
                token,
            );
            onAdded();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to add asset');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.header}>
                    <h2>Add Asset Account</h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        &times;
                    </button>
                </div>

                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.row}>
                        <div className={styles.field}>
                            <label className={styles.label}>Country</label>
                            <select
                                className={styles.input}
                                value={country}
                                onChange={(e) => handleCountryChange(e.target.value as Country)}
                            >
                                {COUNTRIES.map((c) => (
                                    <option key={c.value} value={c.value}>
                                        {c.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.field}>
                            <label className={styles.label}>Account Type</label>
                            <select
                                className={styles.input}
                                value={accountType}
                                onChange={(e) => setAccountType(e.target.value as AccountType)}
                            >
                                {accountTypes.map((t) => (
                                    <option key={t} value={t}>
                                        {ACCOUNT_TYPE_LABELS[t]}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Account Label</label>
                        <input
                            type="text"
                            className={styles.input}
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="e.g., HDFC NRE FD, Fidelity 401k"
                            required
                        />
                    </div>

                    <div className={styles.row}>
                        <div className={styles.field}>
                            <label className={styles.label}>Balance ({currency})</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                className={styles.input}
                                value={formatCurrencyInput(balance, currency)}
                                onChange={(e) => setBalance(extractNumericInput(e.target.value))}
                                required
                            />
                        </div>

                        <div className={styles.field}>
                            <label className={styles.label}>Expected Return (%)</label>
                            <input
                                type="number"
                                className={styles.input}
                                value={expectedReturn}
                                onChange={(e) => setExpectedReturn(e.target.value)}
                                min={-20}
                                max={50}
                                step="0.1"
                                required
                            />
                        </div>
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Ownership</label>
                        <div className={styles.radioGroup}>
                            {(['SELF', 'SPOUSE', 'JOINT'] as const).map((o) => (
                                <label key={o} className={styles.radioLabel}>
                                    <input
                                        type="radio"
                                        name="ownership"
                                        value={o}
                                        checked={ownership === o}
                                        onChange={() => setOwnership(o)}
                                    />
                                    {o.charAt(0) + o.slice(1).toLowerCase()}
                                </label>
                            ))}
                        </div>
                    </div>

                    {error && <p className={styles.error}>{error}</p>}

                    <div className={styles.actions}>
                        <button type="button" className={styles.cancelButton} onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className={styles.submitButton} disabled={saving}>
                            {saving ? 'Adding...' : 'Add Asset'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
